// Walking the layer / group tree. Works on plain documents and on Immer drafts alike.
import { isDraft, original } from 'immer';
import type { Doc, Layer, Node } from './types';

export type Located = { node: Node; siblings: Node[]; index: number; layer: Layer; parent: Node | null };

export function findNode(doc: Doc, id: string): Located | null {
  // On an Immer draft, every property read makes a proxy: search the plain original, then follow the same
  // indices in the draft (a drag in a 10,000-object document stays fast).
  // The recipe may already have moved things: the shortcut is used only if the draft agrees.
  if (isDraft(doc)) {
    const path = findPath(original(doc) as Doc, id);
    const layer = path ? doc.layers[path[0]!] : undefined;
    if (path && layer) {
      let siblings: Node[] | undefined = layer.children;
      let parent: Node | null = null;
      for (let k = 1; k < path.length - 1 && siblings; k++) {
        parent = siblings[path[k]!] ?? null;
        siblings = parent?.kind === 'group' ? parent.children : undefined;
      }
      const index = path[path.length - 1]!;
      const node = siblings?.[index];
      if (siblings && node && node.id === id) return { node, siblings, index, layer, parent };
    }
  }
  for (const layer of doc.layers) {
    const r = findIn(layer.children, id, layer, null);
    if (r) return r;
  }
  return null;
}

/** [layer index, child index, grandchild index, …] of a node, or null. */
export function findPath(doc: Doc, id: string): number[] | null {
  const walk = (list: Node[], path: number[]): number[] | null => {
    for (let i = 0; i < list.length; i++) {
      const n = list[i]!;
      if (n.id === id) return [...path, i];
      if (n.kind === 'group') {
        const r = walk(n.children, [...path, i]);
        if (r) return r;
      }
    }
    return null;
  };
  for (let l = 0; l < doc.layers.length; l++) {
    const r = walk(doc.layers[l]!.children, [l]);
    if (r) return r;
  }
  return null;
}
function findIn(list: Node[], id: string, layer: Layer, parent: Node | null): Located | null {
  for (let i = 0; i < list.length; i++) {
    const n = list[i]!;
    if (n.id === id) return { node: n, siblings: list, index: i, layer, parent };
    if (n.kind === 'group') {
      const r = findIn(n.children, id, layer, n);
      if (r) return r;
    }
  }
  return null;
}

/** Every node, depth-first, back to front. */
export function* allNodes(doc: Doc): Generator<{ node: Node; layer: Layer; depth: number }> {
  for (const layer of doc.layers) yield* walk(layer.children, layer, 0);
}
function* walk(
  list: Node[],
  layer: Layer,
  depth: number,
): Generator<{ node: Node; layer: Layer; depth: number }> {
  for (const n of list) {
    yield { node: n, layer, depth };
    if (n.kind === 'group') yield* walk(n.children, layer, depth + 1);
  }
}

export const countNodes = (doc: Doc) => {
  let n = 0;
  for (const _ of allNodes(doc)) n++;
  return n;
};
